// Data for the Medical / Dental / Pharma landing page
// Target: hospital marketing teams, private practice owners, dental groups, pharma brand managers, medical aid schemes
// Co-branded with OptimaGrowth (SEO growth partner)

export const MEDICAL_CLIENTS = [
  { name: "Roche", note: "Healthcare comms" },
  { name: "Mediclinic", note: "Hospital brand films" },
  { name: "Discovery Health", note: "Member campaigns" },
  { name: "Stellenbosch University", note: "Medical faculty recruitment" },
  { name: "Dis-Chem", note: "Retail pharma content" },
  { name: "Netcare", note: "Patient education" },
  { name: "Aspen Pharmacare", note: "Product launch films" },
  { name: "Private Practice Groups", note: "Dental, derm, aesthetic" },
];

export const MEDICAL_CAPABILITIES = [
  { title: "Patient story films", body: "Real outcomes, real people, ethically captured - the single most powerful asset for medical trust-building.", icon: "Heart" },
  { title: "Procedure & treatment explainers", body: "Calm, clear, beautifully shot walkthroughs that demystify treatments and reduce pre-consult anxiety.", icon: "Stethoscope" },
  { title: "Specialist & clinician profiles", body: "Cinematic short-form bios of your surgeons, dentists and consultants - the modern referral driver.", icon: "UserCheck" },
  { title: "Facility tours & brand films", body: "Show your theatre suites, clinic spaces and patient journey - the silent confidence builder.", icon: "Building2" },
  { title: "Pharma & device launch content", body: "HCP-facing and consumer-facing campaign assets - compliance-aware, multi-market, multi-language.", icon: "Pill" },
  { title: "SEO + content (via OptimaGrowth)", body: "Rank for the procedure searches that actually convert. Medical SEO, treatment landing pages and content velocity.", icon: "Search" },
];

export const MEDICAL_LEAD_MAGNET = {
  badge: "Free for clinics & pharma teams",
  title: "Free Patient-Story Concept + Compliance Treatment",
  subtitle: "Send us your practice, hospital or product brief. In 72 hours we'll deliver a 60-second AI-powered concept reel and a 1-page treatment - covering patient consent, compliance flags, visual approach and shot plan. Yours to use, pitch internally, or build on.",
  bullets: [
    "60-second AI concept reel (vertical + horizontal cuts)",
    "1-page creative treatment with compliance considerations",
    "Patient consent + ethics framework outline",
    "Optional 30-min creative call to walk through it",
    "Delivered in 72 hours, free, no obligation",
  ],
  formTitle: "Get my patient-story concept",
  formSubtitle: "Drop your brief (clinic, hospital, product or campaign) - we'll get on it.",
};

export const MEDICAL_TIERS = [
  {
    badge: "AD HOC",
    name: "Single campaign or launch",
    headline: "For product launches, brand films, single procedures.",
    pricingNote: "Custom-quoted per brief",
    pricingSubnote: "Scoped around your indication, audience and regulatory context.",
    bullets: [
      "Single film or campaign scope",
      "Pre-production through delivery",
      "Patient consent + ethics workflows",
      "Multi-format deliverables (HCP + consumer cuts)",
      "30-day rights buyout standard",
      "Transparent line-item quote",
    ],
    cta: "Send a brief",
    href: "/contact",
  },
  {
    badge: "RETAINER",
    name: "Always-on Practice Growth",
    headline: "Monthly content for clinics, hospital groups and pharma brands.",
    pricingNote: "Monthly retainer · scoped together",
    pricingSubnote: "Volume discount vs ad hoc. Locked priority slots.",
    bullets: [
      "Monthly patient-story or specialist-profile film",
      "AI cutdown engine for social channels",
      "OptimaGrowth medical SEO add-on available",
      "Dedicated producer + edit team",
      "Quarterly content strategy review",
      "Priority booking & rapid turnaround",
    ],
    cta: "Book a strategy call",
    href: null,
    featured: true,
  },
];

export const MEDICAL_FAQS = [
  { q: "How do you handle patient consent and ethics?", a: "Every patient on camera signs a tiered consent form (image, story, identification, usage scope, duration, geography). We work with your clinical lead to flag anything sensitive, and we never film a procedure or interview without written consent in place beforehand. Where ethics board approval is required (e.g. hospital research films), we work to that approval as a precondition." },
  { q: "Can you produce content compliant with HPCSA / SAHPRA / international pharma codes?", a: "Yes. We've delivered HCP and consumer assets that meet SA HPCSA, SAHPRA, and global pharma codes (PhRMA, ABPI, EFPIA). We work with your medical/regulatory affairs team on script and visual review cycles - and we know how to deliver fair balance, ISI handling and indication-aware claims without strangling the creative." },
  { q: "What does the free patient-story concept actually deliver?", a: "You send us your practice or product brief. Within 72 hours we deliver: (1) a 60-second AI-powered concept reel showing the visual direction; (2) a 1-page creative treatment covering visual approach, music direction, shot plan and compliance considerations; (3) an outline patient-consent + ethics framework; (4) optional 30-min creative call. Yours to use however you wish." },
  { q: "Do you film inside hospitals and theatres?", a: "Yes - and frequently. We follow infection control protocols, scrub-in where required, and bring low-footprint camera kit designed for sterile and semi-sterile environments. We've shot in operating theatres, ICU, dental rooms, derm clinics, pharmacies and labs." },
  { q: "How does the OptimaGrowth SEO partnership work for medical?", a: "Medical SEO is a specialist discipline - E-E-A-T signals, treatment-specific landing pages, condition + symptom intent, and locality. OptimaGrowth handles the strategy and on-page work; we produce the video that anchors each treatment page. One brief, one quote, one point of contact." },
  { q: "What about pharma product launches across multiple markets?", a: "We deliver master films in English plus localised subtitled / dubbed cuts for sub-Saharan Africa, MENA, UK/EU and Asia-Pacific. Single shoot day, twenty deliverables, single coherent brand world. We handle market-specific compliance cuts (e.g. ISI/PI variations) in post." },
  { q: "Can you produce at scale across a multi-site hospital group?", a: "Yes. We've run multi-site shoot weeks for hospital groups - capturing facility tours, specialist profiles and patient stories across 4-8 sites in one production block. Retainer pricing scales for this kind of brief." },
  { q: "What happens to my patients' footage and data?", a: "Footage of patients is stored on encrypted drives, never used outside the signed consent scope, and a hard expiry / takedown date is built into every release. We'll send your medico-legal team our standard consent and data handling pack before we shoot." },
];

export const MEDICAL_HERO_IMAGE = "https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=2400&q=80&auto=format&fit=crop";

export const MEDICAL_SHOWCASE = [
  { youtubeId: "85al7Eaq5cM", title: "Vision Will Live - Documentary", category: "Patient Story", tag: "Healthcare NGO" },
  { youtubeId: "NHcp0EBHVEc", title: "Long-form Brand Documentary", category: "Brand Documentary", tag: "Trust Building" },
  { youtubeId: "M0noCv1RZQ8", title: "Specialist Series", category: "Profile Series", tag: "Clinician" },
  { youtubeId: "thZEEMmltaw", title: "Cause-led Music Film", category: "Awareness", tag: "Public Health" },
  { youtubeId: "iNAhiKMGF18", title: "Facility Brand Story", category: "Brand Film", tag: "Hospital" },
  { youtubeId: "KEwcH03tdpA", title: "Cinematic Walk-Through", category: "Facility Tour", tag: "Premium Care" },
];
